import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@alefeducation/ui-elements-library-core';
import Download from '@alefeducation/ui-elements-library-icons/lib/components/Icons/Download';
import Upload from '@alefeducation/ui-elements-library-icons/lib/components/Icons/Upload';
import useCustomRolesCsv from './hooks/useCustomRolesCsv';
import ImportCustomRolesModal from './ImportCustomRolesModal';
import ImportCustomRolesSummaryModal from './ImportCustomRolesSummaryModal';
import CsvOperationProgressBar from './CsvOperationProgressBar';
import { CustomRolesImportSummary } from './utils/importCustomRolesSummary';
import useAllOrganisationsAPI from '../CreateNewRole/hooks/useAllOrganisationsAPI';
import { Organisation } from '../../../apollo/queries';
import {
  EXPORT_CUSTOM_ROLES,
  IMPORT_CUSTOM_ROLES,
} from '../../../utils/constants';

type Props = {
  onImportComplete?: () => void;
  trailingAction?: React.ReactNode;
};

const CustomRolesCsvActions = ({ onImportComplete, trailingAction }: Props) => {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importSummary, setImportSummary] =
    useState<CustomRolesImportSummary | null>(null);
  const { organisations } = useAllOrganisationsAPI();
  const {
    downloadTemplate,
    exportCustomRoles,
    importCustomRoles,
    isExporting,
    isImporting,
    exportProgress,
    importProgress,
  } = useCustomRolesCsv({ onImportComplete });

  const handleImport = async (
    file: File,
    organisation: Pick<Organisation, 'name' | 'code'>,
  ) => {
    const summary = await importCustomRoles(file, organisation);
    if (summary) {
      setIsImportModalOpen(false);
      setImportSummary(summary);
    }
  };

  return (
    <>
      <ActionsContainer>
        <ActionsRow>
          <Button
            variant="ghost"
            size="lg"
            data-testid="export-custom-roles-btn"
            onClick={exportCustomRoles}
            icon={<Download />}
            disabled={isExporting}
          >
            {EXPORT_CUSTOM_ROLES}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            data-testid="import-custom-roles-btn"
            onClick={() => setIsImportModalOpen(true)}
            icon={<Upload />}
            disabled={isImporting}
          >
            {IMPORT_CUSTOM_ROLES}
          </Button>
          {trailingAction}
        </ActionsRow>
        {exportProgress && (
          <CsvOperationProgressBar
            variant="inline"
            data-testid="export-custom-roles-progress"
            progress={exportProgress}
          />
        )}
      </ActionsContainer>
      {isImportModalOpen && (
        <ImportCustomRolesModal
          organisations={organisations}
          isImporting={isImporting}
          importProgress={importProgress}
          onClose={() => setIsImportModalOpen(false)}
          onDownloadTemplate={downloadTemplate}
          onImport={handleImport}
        />
      )}
      {importSummary && (
        <ImportCustomRolesSummaryModal
          summary={importSummary}
          onClose={() => setImportSummary(null)}
        />
      )}
    </>
  );
};

export default CustomRolesCsvActions;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 360px;
`;
